import { haversineDistanceMeters } from './core.mjs';
import { readHybridLootCacheRecords, writeHybridLootCacheRecord } from '../locations/locationCache.mjs';

export const HYBRID_LOOT_SPOTS_API_PATH = '/wp-json/pfandpirat/v1/map-loot-spots';
export const LOOT_SPOT_CACHE_VERSION = 'v1';
export const LOOT_SPOT_SESSION_TTL_MS = 6 * 60 * 60 * 1000;
export const LOOT_SPOT_PERSISTENT_STALE_TTL_MS = 7 * 24 * 60 * 60 * 1000;

const TYPE_META = Object.freeze({
  parkbank: { categoryKey: 'parkbank', categoryLabel: 'Parkbank', modelType: 'hammock', title: 'Parkbank', radius: 20 },
  muelleimer: { categoryKey: 'muelleimer', categoryLabel: 'Mülleimer', modelType: 'barrel', title: 'Mülleimer', radius: 20 },
  supermarkt: { categoryKey: 'supermarkt', categoryLabel: 'Supermarkt', modelType: 'marketStall', title: 'Supermarkt', radius: 25 },
  bushaltestelle: { categoryKey: 'bushaltestelle', categoryLabel: 'Bushaltestelle', modelType: 'pirateShip', title: 'Haltestelle', radius: 30 },
  pfandrueckgabeautomat: { categoryKey: 'pfandrueckgabeautomat', categoryLabel: 'Pfandrückgabeautomat', modelType: 'marketStall', title: 'Pfandrückgabeautomat', radius: 20 },
  recycling: { categoryKey: 'muelleimer', categoryLabel: 'Mülleimer', modelType: 'barrel', title: 'Getränke-Recycling', radius: 20 },
});

function normalizeText(value) {
  return String(value || '').trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
}

function finiteCoordinate(value, min, max) {
  const numeric = Number(value);
  return Number.isFinite(numeric) && numeric >= min && numeric <= max ? numeric : null;
}

function inferPoiType(properties = {}) {
  const values = new Set([
    properties.amenity,
    properties.shop,
    properties.highway,
    properties.public_transport,
    properties.railway,
    properties.vending,
    properties.class,
    properties.subclass,
  ].map(normalizeText).filter(Boolean));
  if (values.has('bench')) return 'parkbank';
  if (values.has('waste_basket')) return 'muelleimer';
  if (values.has('supermarket')) return 'supermarkt';
  if (values.has('reverse_vending_machine')) return 'pfandrueckgabeautomat';
  if (values.has('bus_stop') || values.has('platform') || values.has('stop_position') || values.has('tram_stop')) return 'bushaltestelle';
  if (values.has('recycling') && ['yes', 'only'].some((allowed) => [properties['recycling:beverage_cans'], properties['recycling:glass_bottles'], properties['recycling:plastic_bottles'], properties['recycling:pet']].map(normalizeText).includes(allowed))) return 'recycling';
  return null;
}

function geometryPoint(feature = {}) {
  const coordinates = feature?.geometry?.type === 'Point' ? feature.geometry.coordinates : null;
  if (!Array.isArray(coordinates) || coordinates.length < 2) return null;
  const longitude = finiteCoordinate(coordinates[0], -180, 180);
  const latitude = finiteCoordinate(coordinates[1], -90, 90);
  return latitude === null || longitude === null ? null : { latitude, longitude };
}

export function normalizeVectorTilePoi(feature = {}) {
  const properties = feature.properties || {};
  const type = inferPoiType(properties);
  const point = geometryPoint(feature);
  if (!type || !point) return null;
  const meta = TYPE_META[type];
  const osmType = normalizeText(properties.osm_type);
  const osmId = String(properties.osm_id || '').replace(/\D/g, '');
  const hasStableOsmKey = ['node', 'way', 'relation'].includes(osmType) && /^[1-9][0-9]{0,18}$/.test(osmId);
  const stableFeatureId = feature.id !== null && feature.id !== undefined && String(feature.id).trim() !== '' ? String(feature.id) : '';
  const coordinateKey = `${point.latitude.toFixed(5)}:${point.longitude.toFixed(5)}`;
  const key = hasStableOsmKey
    ? `osm:${osmType}:${osmId}`
    : `vector:poi:${stableFeatureId || `${type}:${coordinateKey}`}`;

  return {
    id: key,
    key,
    source: 'vector',
    sourceType: 'location',
    sourceId: hasStableOsmKey ? `${osmType}:${osmId}` : (stableFeatureId || coordinateKey),
    type,
    title: String(properties.name || meta.title),
    name: String(properties.name || meta.title),
    ...point,
    lat: point.latitude,
    lng: point.longitude,
    categoryKey: meta.categoryKey,
    categoryLabel: meta.categoryLabel,
    category: meta.categoryLabel,
    categories: [meta.categoryLabel],
    modelType: meta.modelType,
    lootable: false,
    lootableNow: false,
    priority: 100,
    sourceLabel: 'Kartendaten',
  };
}

export function normalizeServerOsmSpot(raw = {}) {
  const type = TYPE_META[raw.type] ? raw.type : null;
  const latitude = finiteCoordinate(raw.latitude, -90, 90);
  const longitude = finiteCoordinate(raw.longitude, -180, 180);
  const key = String(raw.key || '');
  if (!type || latitude === null || longitude === null || !/^osm:(node|way|relation):[1-9][0-9]{0,18}$/.test(key)) return null;
  const meta = TYPE_META[type];
  return {
    ...raw,
    id: key,
    key,
    source: 'osm',
    sourceType: 'location',
    title: String(raw.title || meta.title),
    name: String(raw.title || meta.title),
    latitude,
    longitude,
    lat: latitude,
    lng: longitude,
    categoryKey: meta.categoryKey,
    categoryLabel: meta.categoryLabel,
    category: meta.categoryLabel,
    categories: [meta.categoryLabel],
    modelType: meta.modelType,
    lootable: true,
    priority: 200,
    sourceLabel: 'OpenStreetMap',
  };
}

function normalizeWordPressSpot(raw = {}) {
  const id = Number(raw.id ?? raw.location_id);
  if (!Number.isInteger(id) || id <= 0) return null;
  return {
    ...raw,
    id,
    key: `wp:${id}`,
    spotKey: `wp:${id}`,
    source: raw.source || 'wordpress',
    sourceType: 'location',
    lootable: true,
    priority: 300,
  };
}

function sameCategory(a, b) {
  return normalizeText(a.categoryKey || a.categoryLabel || a.type) === normalizeText(b.categoryKey || b.categoryLabel || b.type);
}

function sameName(a, b) {
  const left = normalizeText(a.title || a.name);
  const right = normalizeText(b.title || b.name);
  return Boolean(left && right && left === right);
}

function dedupeRadius(spot) {
  return TYPE_META[spot.type]?.radius || (normalizeText(spot.categoryKey) === 'bushaltestelle' ? 30 : 20);
}

export function mergeLootSpots({ wordpress = [], vector = [], osm = [] } = {}) {
  const confirmedOsm = osm.map(normalizeServerOsmSpot).filter(Boolean);
  const confirmedOsmKeys = new Set(confirmedOsm.map((spot) => spot.key));
  const confirmedVector = vector
    .map((entry) => entry?.sourceType === 'location' ? entry : normalizeVectorTilePoi(entry))
    .filter((entry) => entry && confirmedOsmKeys.has(entry.key));
  const candidates = [
    ...wordpress.map(normalizeWordPressSpot),
    ...confirmedOsm,
    ...confirmedVector,
  ].filter(Boolean).sort((a, b) => Number(b.priority || 0) - Number(a.priority || 0));
  const byKey = new Map();
  for (const spot of candidates) {
    if (!byKey.has(String(spot.key || spot.id))) byKey.set(String(spot.key || spot.id), spot);
  }
  const merged = [];
  for (const spot of byKey.values()) {
    const duplicate = merged.find((existing) => {
      const distance = haversineDistanceMeters(existing, spot);
      if (distance > Math.max(dedupeRadius(existing), dedupeRadius(spot))) return false;
      if (existing.priority === 300) return sameCategory(existing, spot) || sameName(existing, spot);
      return sameCategory(existing, spot) && (sameName(existing, spot) || normalizeText(existing.categoryKey) === 'bushaltestelle');
    });
    if (!duplicate) merged.push(spot);
  }
  return merged;
}

function sessionKey(bboxKey) {
  return `pfp:hybrid-loot:${LOOT_SPOT_CACHE_VERSION}:${bboxKey}`;
}

export function readLootSpotSessionCache(bboxKey, storage = globalThis?.sessionStorage) {
  if (!bboxKey || !storage?.getItem) return null;
  try {
    const entry = JSON.parse(storage.getItem(sessionKey(bboxKey)) || 'null');
    if (!entry || !Array.isArray(entry.spots) || Date.now() - Number(entry.savedAt || 0) > LOOT_SPOT_SESSION_TTL_MS) return null;
    return entry;
  } catch {
    return null;
  }
}

export function writeLootSpotSessionCache(bboxKey, entry, storage = globalThis?.sessionStorage) {
  if (!bboxKey || !storage?.setItem) return;
  try {
    storage.setItem(sessionKey(bboxKey), JSON.stringify({ ...entry, savedAt: Date.now() }));
  } catch {
    // Sitzungscache ist eine Optimierung und darf die Karte niemals blockieren.
  }
}

export async function readCachedServerOsmSpots({ bbox, indexedDb = globalThis?.indexedDB } = {}) {
  if (!bbox?.key) return null;
  const records = await readHybridLootCacheRecords(bbox, indexedDb);
  const record = records.find((candidate) => candidate.key === bbox.key) || records[0] || null;
  if (!record || Date.now() - Number(record.updatedAt || 0) > LOOT_SPOT_PERSISTENT_STALE_TTL_MS) return null;
  return {
    spots: record.spots,
    etag: record.etag || '',
    meta: { ...record.meta, cache: 'indexeddb', partial: false, updatedAt: record.updatedAt },
  };
}

export async function fetchServerOsmSpots({ bbox, endpoint = HYBRID_LOOT_SPOTS_API_PATH, nonce = '', enabled = true, signal, fetchImpl = globalThis.fetch, storage = globalThis?.sessionStorage, indexedDb = globalThis?.indexedDB } = {}) {
  if (!enabled) return { spots: [], meta: { cache: 'disabled', partial: false } };
  if (!bbox?.key || typeof fetchImpl !== 'function') return { spots: [], meta: { cache: 'unavailable', partial: true } };
  const persistent = await readCachedServerOsmSpots({ bbox, indexedDb });
  const cached = persistent || readLootSpotSessionCache(bbox.key, storage);
  const headers = { Accept: 'application/json' };
  if (nonce) headers['X-WP-Nonce'] = nonce;
  if (cached?.etag) headers['If-None-Match'] = cached.etag;
  const params = new URLSearchParams({ bbox: bbox.key });
  try {
    const response = await fetchImpl(`${endpoint}?${params.toString()}`, { credentials: 'include', signal, headers });
    if (response.status === 304 && cached) return { spots: cached.spots, meta: { ...cached.meta, cache: persistent ? 'indexeddb-revalidated' : 'session-revalidated', partial: false } };
    if (!response.ok) throw new Error(`POI-API antwortete mit HTTP ${response.status}.`);
    const payload = await response.json();
    const spots = Array.isArray(payload?.spots) ? payload.spots : [];
    const etag = response.headers?.get?.('ETag') || '';
    writeLootSpotSessionCache(bbox.key, { spots, etag, meta: payload?.meta || {} }, storage);
    await writeHybridLootCacheRecord({ bbox, spots, etag, meta: payload?.meta || {} }, indexedDb);
    return { spots, meta: payload?.meta || { cache: 'network', partial: false } };
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    if (cached) return { spots: cached.spots, meta: { ...cached.meta, cache: persistent ? 'indexeddb-stale' : 'session-stale', partial: true, error: error?.message || 'provider-error' } };
    return { spots: [], meta: { cache: 'error', partial: true, error: error?.message || 'provider-error' } };
  }
}

export function normalizeVectorTilePois(features = []) {
  const byKey = new Map();
  for (const feature of Array.isArray(features) ? features : []) {
    const spot = normalizeVectorTilePoi(feature);
    if (spot && !byKey.has(spot.key)) byKey.set(spot.key, spot);
  }
  return [...byKey.values()];
}
